/* eslint-disable guard-for-in */
import { PreOrderRequestsListModel } from "@features/pre-order/data/models";
import { BaseRepository } from "@features/shared/data";
import {
  PreOrderExternalProductRequestPayload,
  PreOrderListParams,
  PreOrderRepositoryContract,
  PreOrderRequestPayload,
  PreOrderRequestsListData,
  PreOrderStatuses,
  PreOrderSummaryEntity,
} from "../../domain";
import { ALLOWED_PRE_ORDER_STATUSES, preOrderAPI } from "../constants";
import {
  mapPreOrderRequestsListData,
  mapPreOrderSummaryData,
} from "../mappers";
import { PreOrderSummaryModel } from "../models/pre-order-summary-model";

export class PreOrderRepository
  extends BaseRepository
  implements PreOrderRepositoryContract
{
  public async list(
    params?: PreOrderListParams,
  ): Promise<PreOrderRequestsListData> {
    const { data } = await this.http.get<PreOrderRequestsListModel>(
      preOrderAPI.list,
      {
        params,
      },
    );

    return mapPreOrderRequestsListData(data);
  }

  public async listSummary(): Promise<PreOrderSummaryEntity[]> {
    const { data } = await this.http.get<{
      data: { preorderStatusSummary: PreOrderSummaryModel[] };
    }>(preOrderAPI.listSummary);

    return data.data.preorderStatusSummary
      .filter(summary => ALLOWED_PRE_ORDER_STATUSES.includes(summary.status))
      .sort((a, b) => {
        const indexA = ALLOWED_PRE_ORDER_STATUSES.indexOf(a.status);
        const indexB = ALLOWED_PRE_ORDER_STATUSES.indexOf(b.status);
        return indexA - indexB;
      })
      .map(mapPreOrderSummaryData);
  }

  public async listUpcomingRequests(
    params?: PreOrderListParams,
  ): Promise<PreOrderRequestsListData> {
    const { data } = await this.http.get<PreOrderRequestsListModel>(
      preOrderAPI.listUpcomingRequests,
      {
        params: {
          ...params,
          status: PreOrderStatuses.DOWN_PAYMENT_OFFERED,
        },
      },
    );

    return mapPreOrderRequestsListData(data, params?.pageSize);
  }

  public async listRequests(
    params?: PreOrderListParams,
  ): Promise<PreOrderRequestsListData> {
    const { data } = await this.http.get<PreOrderRequestsListModel>(
      preOrderAPI.listRequests,
      {
        params: {
          ...params,
          excludeStatus: PreOrderStatuses.DOWN_PAYMENT_OFFERED,
        },
      },
    );

    return mapPreOrderRequestsListData(data, params?.pageSize);
  }

  public async createPreOrderRequest(
    data: PreOrderRequestPayload,
  ): Promise<void> {
    await this.http.post(preOrderAPI.createPreOrderRequest, data);
  }

  public async createExternalProductPreOrderRequest(
    data: PreOrderExternalProductRequestPayload,
  ): Promise<void> {
    await this.http.post(
      preOrderAPI.createExternalProductPreOrderRequest,
      data,
    );
  }

  public async rejectPreOrderRequest(id: string): Promise<void> {
    await this.http.put(preOrderAPI.rejectPreOrderRequest(id), {});
  }

  public async acceptPreOrderOffer(
    requestId: string,
    offerId: number,
  ): Promise<void> {
    await this.http.post(preOrderAPI.acceptPreOrderOffer(requestId), {
      offerId,
    });
  }
}
